/**
 * V52/V53 — Foreign / NRI country → curriculum → board → suggested-subjects cascade.
 *
 * Renders ONLY inside the Foreign/NRI branch of the registration form. The
 * standard India home-tuition form is untouched.
 *
 * V53 — Now loads the country/curriculum/board tree from the backend
 * Master Data API (`/api/master/curriculum_pack`) so admins can manage it
 * from the dashboard without code changes. Falls back to the V52 hardcoded
 * lib if the API is unreachable or empty (zero-downtime migration).
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { GraduationCap, BookOpen, Layers } from "lucide-react";
import { FOREIGN_CURRICULUM_MAP as FALLBACK_MAP } from "../lib/foreignCurriculum";
import { partitionBoards } from "../data/popularBoardsByCountry";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Normalise an API pack row to the same shape the local lib uses.
function normalisePackRow(row) {
    return {
        code: row.code,
        name: row.value,
        flag: row.extra?.flag || "",
        defaultCurriculumId: row.extra?.default_curriculum_id || row.extra?.defaultCurriculumId,
        curricula: row.extra?.curricula || [],
    };
}

export default function ForeignCurriculumSelector({ value, onChange, phoneCountryHint = "" }) {
    const [apiMap, setApiMap] = useState(null);   // null until first fetch resolves
    // V130 — Track whether the current country_code was set by the phone-flag
    // auto-fill so subsequent phone changes can update it, while a manual pick
    // from the country dropdown is respected forever.
    const [autofillIso, setAutofillIso] = useState("");

    useEffect(() => {
        let cancelled = false;
        axios.get(`${API}/master/curriculum_pack`)
            .then((res) => {
                if (cancelled) return;
                const items = Array.isArray(res.data) ? res.data : (res.data.items || []);
                if (!items.length) { setApiMap({}); return; }
                const map = {};
                for (const row of items) {
                    map[row.code] = normalisePackRow(row);
                }
                setApiMap(map);
            })
            .catch(() => { if (!cancelled) setApiMap({}); });
        return () => { cancelled = true; };
    }, []);

    // Choose the live data source: API map (if non-empty) → fallback to hardcoded lib.
    const dataMap = (apiMap && Object.keys(apiMap).length > 0) ? apiMap : FALLBACK_MAP;
    const countryOptions = useMemo(() => {
        const src = (apiMap && Object.keys(apiMap).length > 0) ? apiMap : FALLBACK_MAP;
        return Object.entries(src)
            .map(([code, v]) => ({ code, name: v.name, flag: v.flag }))
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [apiMap]);

    const {
        country_code = "",
        curriculum_id = "",
        board_id = "",
        subjects = "",
    } = value || {};

    const pack = country_code ? dataMap[country_code] : null;
    const curriculumOptions = pack ? pack.curricula : [];
    const activeCurriculum = useMemo(
        () => curriculumOptions.find((c) => c.id === curriculum_id) || null,
        [curriculumOptions, curriculum_id],
    );
    const boardOptions = activeCurriculum ? activeCurriculum.boards : [];
    const activeBoard = useMemo(
        () => boardOptions.find((b) => b.id === board_id) || null,
        [boardOptions, board_id],
    );

    const setCountry = (code) => {
        const next = dataMap[code];
        if (!next) {
            onChange({
                country_code: code, country_name: "",
                curriculum_id: "", curriculum_label: "",
                board_id: "", board_label: "",
                subjects: "",
            });
            return;
        }
        const defCur = next.curricula.find((c) => c.id === next.defaultCurriculumId) || next.curricula[0];
        onChange({
            country_code: code,
            country_name: next.name,
            curriculum_id: defCur?.id || "",
            curriculum_label: defCur?.label || "",
            board_id: "",
            board_label: "",
            subjects: "",
        });
    };

    // V130 — Auto-apply the phone-country hint whenever it changes AND either
    // (a) the country selector is empty, or (b) the current country_code came
    // from this same auto-fill path. Manual picks (tracked by clearing
    // `autofillIso` on every user-driven `setCountry`) are never overwritten.
    useEffect(() => {
        const iso = (phoneCountryHint || "").toUpperCase();
        if (!iso) return;
        if (!dataMap[iso]) return;   // don't seed if we don't know the country
        const currentIsEmpty = !country_code;
        const currentIsAutofilled = country_code && country_code === autofillIso;
        if (!currentIsEmpty && !currentIsAutofilled) return;
        if (country_code === iso) return;   // already set to the same iso
        setCountry(iso);
        setAutofillIso(iso);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [phoneCountryHint, dataMap, country_code]);

    // Wrap the user-facing country-dropdown setter so we clear the autofill
    // marker on any manual pick — a subsequent phone-flag flip will not
    // silently overwrite the parent's explicit choice.
    const setCountryFromDropdown = (code) => {
        setAutofillIso("");
        setCountry(code);
    };

    const setCurriculum = (id) => {
        const cur = curriculumOptions.find((c) => c.id === id) || null;
        onChange({
            curriculum_id: cur?.id || "",
            curriculum_label: cur?.label || "",
            board_id: "",
            board_label: "",
            subjects: "",
        });
    };

    const setBoard = (id) => {
        const b = boardOptions.find((x) => x.id === id) || null;
        onChange({
            board_id: b?.id || "",
            board_label: b?.label || "",
            // Pre-fill suggested subjects (parent can edit freely)
            subjects: b?.suggestedSubjects ? b.suggestedSubjects.join(", ") : subjects,
        });
    };

    const toggleSubject = (s) => {
        const arr = subjects ? subjects.split(",").map((x) => x.trim()).filter(Boolean) : [];
        const idx = arr.findIndex((x) => x.toLowerCase() === s.toLowerCase());
        if (idx >= 0) arr.splice(idx, 1);
        else arr.push(s);
        onChange({ subjects: arr.join(", ") });
    };

    const currentSubjects = subjects ? subjects.split(",").map((x) => x.trim()).filter(Boolean) : [];

    return (
        <div className="space-y-3" data-testid="foreign-curriculum-selector">
            {/* Country */}
            <label className="block">
                <span className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500 mb-1.5 block">
                    Country of Study *
                </span>
                <select
                    value={country_code}
                    onChange={(e) => setCountryFromDropdown(e.target.value)}
                    data-testid="foreign-curr-country"
                    className={selectCls}
                >
                    <option value="">Select country…</option>
                    {countryOptions.map((o) => (
                        <option key={o.code} value={o.code}>
                            {o.flag} {o.name}
                        </option>
                    ))}
                </select>
                {/* V130 — Green auto-detected hint (mirrors V126/V127/V128 UX)
                    when the country of study was seeded from the phone-flag pick
                    and matches the currently-selected value. Manual overrides
                    clear `autofillIso`, immediately hiding this hint. */}
                {autofillIso && autofillIso === country_code && pack && (
                    <p
                        className="mt-1.5 text-[11px] text-emerald-700"
                        data-testid="foreign-curr-country-auto-hint"
                    >
                        Country of study auto-detected from phone code: <strong>{pack.name}</strong>
                        {activeCurriculum && (
                            <> · Pre-selected curriculum: <strong>{activeCurriculum.label}</strong></>
                        )}
                    </p>
                )}
            </label>

            {/* Curriculum — only shown after country */}
            {pack && curriculumOptions.length > 0 && (
                <label className="block" data-testid="foreign-curr-curriculum-wrap">
                    <span className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500 mb-1.5 block">
                        <GraduationCap className="w-3.5 h-3.5 inline -mt-0.5 mr-1" />
                        Curriculum *
                    </span>
                    {curriculumOptions.length === 1 ? (
                        <div className="px-3 py-2.5 text-sm rounded-lg border border-blue-200 bg-blue-50 text-[#1E3A8A] font-semibold" data-testid="foreign-curr-curriculum-static">
                            {curriculumOptions[0].label}
                        </div>
                    ) : (
                        <select
                            value={curriculum_id}
                            onChange={(e) => setCurriculum(e.target.value)}
                            data-testid="foreign-curr-curriculum"
                            className={selectCls}
                        >
                            {curriculumOptions.map((c) => (
                                <option key={c.id} value={c.id}>{c.label}</option>
                            ))}
                        </select>
                    )}
                </label>
            )}

            {/* Board — only shown when curriculum has boards */}
            {activeCurriculum && boardOptions.length > 0 && (() => {
                // V131 — Country-Aware Board Suggestions. Split the current
                // curriculum's boards into a "Popular in <Country>" group at
                // the top (top-3 for the auto-detected country) and a "More
                // boards" group below. Nothing is hidden or renamed.
                const { popular, others } = partitionBoards(country_code, boardOptions);
                const hasPopular = popular.length > 0;
                return (
                    <label className="block" data-testid="foreign-curr-board-wrap">
                        <span className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500 mb-1.5 block">
                            <BookOpen className="w-3.5 h-3.5 inline -mt-0.5 mr-1" />
                            Board / Programme *
                        </span>
                        <select
                            value={board_id}
                            onChange={(e) => setBoard(e.target.value)}
                            data-testid="foreign-curr-board"
                            className={selectCls}
                        >
                            <option value="">Select board / programme…</option>
                            {hasPopular ? (
                                <>
                                    <optgroup
                                        label={`⭐ Popular in ${pack?.name || "your country"}`}
                                        data-testid="foreign-curr-board-popular-group"
                                    >
                                        {popular.map((b) => (
                                            <option key={b.id} value={b.id} data-testid={`foreign-curr-board-opt-popular-${b.id}`}>
                                                {b.label}
                                            </option>
                                        ))}
                                    </optgroup>
                                    <optgroup label="More boards">
                                        {others.map((b) => (
                                            <option key={b.id} value={b.id} data-testid={`foreign-curr-board-opt-${b.id}`}>
                                                {b.label}
                                            </option>
                                        ))}
                                    </optgroup>
                                </>
                            ) : (
                                boardOptions.map((b) => (
                                    <option key={b.id} value={b.id} data-testid={`foreign-curr-board-opt-${b.id}`}>
                                        {b.label}
                                    </option>
                                ))
                            )}
                        </select>
                        {hasPopular && !board_id && (
                            <p
                                className="mt-1.5 text-[11px] text-emerald-700"
                                data-testid="foreign-curr-board-popular-hint"
                            >
                                Top picks in <strong>{pack?.name}</strong>:{" "}
                                {popular.map((b) => b.label).join(" · ")}
                            </p>
                        )}
                    </label>
                );
            })()}

            {/* Suggested subjects — chips */}
            {activeBoard && activeBoard.suggestedSubjects?.length > 0 && (
                <div data-testid="foreign-curr-subjects-wrap">
                    <div className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500 mb-1.5 flex items-center gap-1">
                        <Layers className="w-3.5 h-3.5" />
                        Suggested subjects for {activeBoard.label}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                        {activeBoard.suggestedSubjects.map((s) => {
                            const isOn = currentSubjects.some((x) => x.toLowerCase() === s.toLowerCase());
                            return (
                                <button
                                    type="button"
                                    key={s}
                                    onClick={() => toggleSubject(s)}
                                    data-testid={`foreign-curr-subject-${s.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                                    className={`text-xs px-2.5 py-1.5 rounded-full border transition-colors ${
                                        isOn
                                            ? "bg-[#1E3A8A] text-white border-[#1E3A8A]"
                                            : "bg-white border-slate-200 text-slate-700 hover:border-[#1E3A8A] hover:text-[#1E3A8A]"
                                    }`}
                                >
                                    {s}
                                </button>
                            );
                        })}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-1.5">
                        Tap to add/remove. You can also edit the Subjects field below freely.
                    </div>
                </div>
            )}
        </div>
    );
}

const selectCls =
    "w-full px-3 py-2.5 text-sm rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#3B82F6]";
